import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { handleFirestoreError } from './handleFirestoreError';

type Status = 'idle' | 'loading' | 'fetching' | 'error' | 'critical-error' | 'success';

type QueryHelper<TData = unknown> = {
  queryStatus: Status;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isCriticalError: boolean;
  errorMessage: string | null;
  previousData: TData | null;
};

type Query<TData = unknown, TError = AxiosError> = UseQueryResult<TData, TError>;

type QueryHelperParams<TData = unknown, TError = AxiosError> = {
  queries: Query<TData, TError>[];
  enabledStatus?: boolean[];
  initialState?: Status;
};

const isQueryEnabled = (enabledStatus: boolean[] | undefined, idx: number): boolean => {
  if (!enabledStatus || enabledStatus.length <= idx) return true;
  return enabledStatus[idx];
};

export const useQueryHelper = <TData, TError = AxiosError>({
  queries,
  enabledStatus,
  initialState = 'idle',
}: QueryHelperParams<TData, TError>): QueryHelper<TData> => {
  const [previousData, setPreviousData] = useState<TData | null>(null);

  // Filtruj tylko włączone query
  const enabledQueries = useMemo(
    () => queries.filter((_, idx) => isQueryEnabled(enabledStatus, idx)),
    [queries, enabledStatus],
  );

  // Sprawdź czy którekolwiek query ma dane
  const hasData = enabledQueries.some((q) => q.data !== undefined && q.data !== null);

  // Loading - pierwsze ładowanie (brak danych)
  const isLoading = enabledQueries.some((q) => q.isLoading);

  // Fetching - odświeżanie w tle (dane mogą istnieć)
  const isFetching = enabledQueries.some((q) => q.isFetching);

  // Error - gdy którekolwiek query ma błąd
  const hasError = enabledQueries.some((q) => q.isError);

  // Critical error - błąd inny niż 404
  const hasCriticalError = enabledQueries.some(
    (q) => q.isError && (q.error as unknown as AxiosError)?.response?.status !== 404,
  );

  // Oblicz status
  const queryStatus = useMemo((): Status => {
    if (isLoading) return 'loading';
    if (isFetching && !hasData) return 'fetching';
    if (hasCriticalError) return 'critical-error';
    if (hasError) return 'error';
    if (hasData) return 'success';
    return initialState;
  }, [isLoading, isFetching, hasData, hasCriticalError, hasError, initialState]);

  // Error message
  const errorMessage = useMemo(() => {
    if (hasCriticalError) return handleFirestoreError('critical-error');
    return null;
  }, [hasCriticalError]);

  // Aktualizuj previousData gdy pojawią się nowe dane
  useEffect(() => {
    const latestData = enabledQueries.find((q) => q.data)?.data as TData | undefined;
    if (latestData) {
      setPreviousData(latestData);
    }
  }, [enabledQueries]);

  return {
    queryStatus,
    errorMessage,
    isLoading,
    isFetching,
    isError: hasError || hasCriticalError,
    isCriticalError: hasCriticalError,
    previousData,
  };
};
